import API_BASE from '../../utils/api';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DEFAULT_CATEGORIES = ['South Indian Festivals', 'National Festivals', 'Summer Holidays', 'Winter Holidays', 'Others'];

const APPLICABLE_TO_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'students', label: 'Students' },
  { value: 'teachers', label: 'Teachers' }
];

const emptyForm = { title: '', description: '', eventDate: '', endDate: '', category: '', applicableTo: 'all', visibleToTeachers: false };

const HolidayCalendar = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const categoryOptions = Array.from(new Set([
    ...DEFAULT_CATEGORIES,
    ...holidays.map(h => h.category).filter(Boolean)
  ]));

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/events/holidays`);
      setHolidays(res.data || []);
    } catch (_) {
      setError('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const openAdd = () => {
    setEditingHoliday(null);
    setForm(emptyForm);
    setFieldErrors({});
    setShowModal(true);
  };

  const openEdit = (holiday) => {
    setEditingHoliday(holiday);
    setForm({
      title: holiday.title,
      description: holiday.description,
      eventDate: holiday.eventDate?.slice(0, 10) || '',
      endDate: holiday.endDate?.slice(0, 10) || '',
      category: holiday.category || '',
      applicableTo: holiday.applicableTo || 'all',
      visibleToTeachers: holiday.visibleToTeachers || false
    });
    setFieldErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!form.title.trim()) errors.title = 'Holiday name is required';
    if (!form.eventDate) errors.eventDate = 'Start date is required';
    if (!form.endDate) errors.endDate = 'End date is required';
    if (form.eventDate && form.endDate && form.endDate < form.eventDate) {
      errors.endDate = 'End date cannot be before start date';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setError('Please fix the errors below');
      return;
    }
    setSaving(true);
    try {
      if (editingHoliday) {
        await axios.put(`${API_BASE}/events/${editingHoliday._id}`, { ...form, eventType: 'holiday', isSchoolWide: true });
        setSuccess('Holiday updated');
      } else {
        await axios.post(`${API_BASE}/events`, { ...form, eventType: 'holiday', isSchoolWide: true });
        setSuccess('Holiday added');
      }
      setShowModal(false);
      fetchHolidays();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await axios.delete(`${API_BASE}/events/${id}`);
      setSuccess('Holiday deleted');
      fetchHolidays();
      setTimeout(() => setSuccess(''), 3000);
    } catch (_) {
      setError('Failed to delete');
    }
  };

  const handleToggleVisibility = async (holiday) => {
    try {
      const res = await axios.patch(`${API_BASE}/events/${holiday._id}/toggle-teacher-visibility`);
      setHolidays(prev => prev.map(h => h._id === holiday._id ? { ...h, visibleToTeachers: res.data.visibleToTeachers } : h));
    } catch (_) {
      setError('Failed to update visibility');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  };

  const groupByMonth = (list) => {
    return list.reduce((acc, h) => {
      const d = new Date(h.eventDate);
      const key = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(h);
      return acc;
    }, {});
  };

  const grouped = groupByMonth(holidays);
  const visibleCount = holidays.filter(h => h.visibleToTeachers).length;

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Holiday Calendar</h1>
              <p className="text-sm text-gray-500">Manage school holidays and control teacher visibility</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={openAdd}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
              >
                + Add Holiday
              </button>
              <ProfileDropdown />
            </div>
          </div>
        </nav>

        <div className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">{error}<button className="ml-2 underline" onClick={() => setError('')}>Dismiss</button></div>}
          {success && <div className="p-3 bg-green-100 text-green-700 rounded-lg text-sm">{success}</div>}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase font-semibold">Total Holidays</p>
              <p className="text-3xl font-bold text-indigo-600 mt-1">{holidays.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase font-semibold">Visible to Teachers</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{visibleCount}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase font-semibold">Hidden from Teachers</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">{holidays.length - visibleCount}</p>
            </div>
          </div>

          {/* Holiday list grouped by month */}
          {loading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <div className="flex justify-center items-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                Loading...
              </div>
            </div>
          ) : holidays.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
              <div className="text-5xl mb-3">📅</div>
              <p className="font-medium">No holidays added yet</p>
              <p className="text-sm mt-1">Click "Add Holiday" to get started</p>
            </div>
          ) : (
            Object.entries(grouped).map(([month, items]) => (
              <div key={month} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-5 py-3 bg-indigo-50 border-b border-indigo-100">
                  <h3 className="font-semibold text-indigo-800">{month}</h3>
                </div>
                <div className="divide-y">
                  {items.map(holiday => (
                    <div key={holiday._id} className="flex items-center px-5 py-4 hover:bg-gray-50 gap-4">
                      {/* Date badge */}
                      <div className="shrink-0 w-14 text-center">
                        <div className="text-lg font-bold text-indigo-700">{new Date(holiday.eventDate).getDate()}</div>
                        <div className="text-xs text-gray-500">{MONTHS[new Date(holiday.eventDate).getMonth()]}</div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">{holiday.title}</p>
                        {holiday.endDate && holiday.endDate.slice(0, 10) !== holiday.eventDate.slice(0, 10) && (
                          <p className="text-xs text-gray-500">{formatDate(holiday.eventDate)} - {formatDate(holiday.endDate)}</p>
                        )}
                        <p className="text-sm text-gray-500 truncate">{holiday.description}</p>
                        <div className="flex gap-2 mt-1">
                          {holiday.category && (
                            <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{holiday.category}</span>
                          )}
                          {holiday.applicableTo && holiday.applicableTo !== 'all' && (
                            <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full capitalize">{holiday.applicableTo}</span>
                          )}
                        </div>
                      </div>

                      {/* Teacher visibility toggle */}
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-gray-500">Teachers:</span>
                        <button
                          onClick={() => handleToggleVisibility(holiday)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${holiday.visibleToTeachers ? 'bg-green-500' : 'bg-gray-300'}`}
                          title={holiday.visibleToTeachers ? 'Visible to teachers — click to hide' : 'Hidden from teachers — click to show'}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${holiday.visibleToTeachers ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className={`text-xs font-medium ${holiday.visibleToTeachers ? 'text-green-600' : 'text-gray-400'}`}>
                          {holiday.visibleToTeachers ? 'On' : 'Off'}
                        </span>
                      </div>

                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => openEdit(holiday)} className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">Edit</button>
                        <button onClick={() => handleDelete(holiday._id)} className="text-red-600 hover:text-red-800 text-sm font-medium">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${fieldErrors.title ? 'border-red-400 focus:ring-red-400' : 'focus:ring-indigo-500'}`}
                  placeholder="e.g. Independence Day"
                />
                {fieldErrors.title && <p className="text-xs text-red-600 mt-1">{fieldErrors.title}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={form.eventDate}
                    onChange={e => setForm(f => ({ ...f, eventDate: e.target.value }))}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${fieldErrors.eventDate ? 'border-red-400 focus:ring-red-400' : 'focus:ring-indigo-500'}`}
                  />
                  {fieldErrors.eventDate && <p className="text-xs text-red-600 mt-1">{fieldErrors.eventDate}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${fieldErrors.endDate ? 'border-red-400 focus:ring-red-400' : 'focus:ring-indigo-500'}`}
                  />
                  {fieldErrors.endDate && <p className="text-xs text-red-600 mt-1">{fieldErrors.endDate}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  list="holiday-category-options"
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Select or type a new category"
                />
                <datalist id="holiday-category-options">
                  {categoryOptions.map(opt => <option key={opt} value={opt} />)}
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Applicable To</label>
                <select
                  value={form.applicableTo}
                  onChange={e => setForm(f => ({ ...f, applicableTo: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {APPLICABLE_TO_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Optional details about this holiday"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, visibleToTeachers: !f.visibleToTeachers }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.visibleToTeachers ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.visibleToTeachers ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm text-gray-700">
                  {form.visibleToTeachers ? 'Visible to teachers' : 'Hidden from teachers'}
                </span>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                  {saving ? 'Saving...' : editingHoliday ? 'Update' : 'Add Holiday'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayCalendar;
